import { isExist, getTimestamp } from 'common/utils';
import { Action, InventoryActions, InventoryItem, StockMovement, Recipe } from 'common/types';
import { db } from 'firebase/config';
import { setDocWithCreatedBy } from 'firebase/utils';
import { collection, doc, updateDoc, deleteDoc, CollectionReference, DocumentData } from 'firebase/firestore';

// Firestore collections
const inventoryItemsCollection: CollectionReference<DocumentData> = collection(db, 'inventoryItems');
const stockMovementsCollection: CollectionReference<DocumentData> = collection(db, 'stockMovements');
const recipesCollection: CollectionReference<DocumentData> = collection(db, 'recipes');

// Helper function to update lastModifiedTime
function updateTimestamp<T extends { lastModifiedTime?: number }>(item: T): T {
  const updatedItem = { ...item };
  updatedItem.lastModifiedTime = getTimestamp();
  return updatedItem;
}

export const createInventoryActions: Action<InventoryActions> = (state, updateState) => {
  // Create the actions object
  const actions: InventoryActions = {
    // Inventory items
    addInventoryItem: async (item) => {
      const newItem: InventoryItem = {
        ...item,
        id: '', // Will be set by setDocWithCreatedBy
        lastModifiedTime: getTimestamp(),
        isDeleted: false
      };
      
      const createdItem = await setDocWithCreatedBy(inventoryItemsCollection, newItem);
      
      // Update state
      updateState({
        inventoryItems: [...(state.inventoryItems || []), createdItem]
      });
      
      return createdItem;
    },
    
    updateInventoryItem: async (item) => {
      const updatedItem = updateTimestamp(item);
      
      // Update in Firestore
      const itemRef = doc(inventoryItemsCollection, item.id);
      await updateDoc(itemRef, updatedItem);
      
      // Update state
      const restItems = [...(state.inventoryItems || []).filter(entity => entity.id !== item.id)];
      updateState({
        inventoryItems: [...restItems, updatedItem]
      });
    },
    
    removeInventoryItem: async (itemId) => {
      // Delete from Firestore
      const itemRef = doc(inventoryItemsCollection, itemId);
      await deleteDoc(itemRef);
      
      // Update state (mark as deleted)
      const updItems = [...(state.inventoryItems || [])];
      const targetIndex = updItems.findIndex(entity => entity.id === itemId);
      
      if (!isExist(targetIndex)) throw new Error('The specified inventory item does not exist');
      
      updItems[targetIndex] = {
        ...updItems[targetIndex],
        isDeleted: true,
        lastModifiedTime: getTimestamp()
      };
      
      updateState({
        inventoryItems: updItems
      });
    },
    
    // Stock movements
    addStockMovement: async (movement) => {
      const newMovement: StockMovement = {
        ...movement,
        id: '', // Will be set by setDocWithCreatedBy
        date: getTimestamp()
      };
      
      const createdMovement = await setDocWithCreatedBy(stockMovementsCollection, newMovement);
      
      // Update inventory item quantity
      const inventoryItems = [...(state.inventoryItems || [])];
      const itemIndex = inventoryItems.findIndex(item => item.id === movement.inventoryItemId);
      
      if (isExist(itemIndex)) {
        const item = inventoryItems[itemIndex];
        const updatedItem = {
          ...item,
          quantity: item.quantity + movement.quantity,
          lastModifiedTime: getTimestamp()
        };
        
        // Update item in Firestore
        const itemRef = doc(inventoryItemsCollection, item.id);
        await updateDoc(itemRef, { quantity: updatedItem.quantity, lastModifiedTime: updatedItem.lastModifiedTime });
        
        // Update state
        inventoryItems[itemIndex] = updatedItem;
        updateState({
          inventoryItems,
          stockMovements: [...(state.stockMovements || []), createdMovement]
        });
      } else {
        // Just update movements if item not found in state
        updateState({
          stockMovements: [...(state.stockMovements || []), createdMovement]
        });
      }
      
      return createdMovement;
    },
    
    // Recipes
    addRecipe: async (recipe) => {
      const newRecipe: Recipe = {
        ...recipe,
        id: '' // Will be set by setDocWithCreatedBy
      };
      
      const createdRecipe = await setDocWithCreatedBy(recipesCollection, newRecipe);
      
      // Update state
      updateState({
        recipes: [...(state.recipes || []), createdRecipe]
      });
      
      return createdRecipe;
    },
    
    updateRecipe: async (recipe) => {
      // Update in Firestore
      const recipeRef = doc(recipesCollection, recipe.id);
      await updateDoc(recipeRef, recipe);
      
      // Update state
      const restRecipes = [...(state.recipes || []).filter(entity => entity.id !== recipe.id)];
      updateState({
        recipes: [...restRecipes, recipe]
      });
    },
    
    removeRecipe: async (recipeId) => {
      // Delete from Firestore
      const recipeRef = doc(recipesCollection, recipeId);
      await deleteDoc(recipeRef);
      
      // Update state
      const updRecipes = [...(state.recipes || []).filter(entity => entity.id !== recipeId)];
      updateState({
        recipes: updRecipes
      });
    },
    
    // Reduce inventory based on product sale
    reduceInventoryFromSale: async (productId: string, quantity: number) => {
      // Find if the product has a recipe
      const recipe = (state.recipes || []).find(r => r.productId === productId);
      
      if (recipe) {
        // If the product has a recipe, reduce inventory for each ingredient
        for (const ingredient of recipe.ingredients) {
          // Create a stock movement for each ingredient
          const movement: Omit<StockMovement, 'id' | 'date'> = {
            inventoryItemId: ingredient.inventoryItemId,
            quantity: -1 * ingredient.quantity * quantity, // Negative quantity for consumption
            type: 'sale',
            notes: `Consumed in product sale (Product ID: ${productId})`
          };
          
          // Use the addStockMovement function from this actions object
          await actions.addStockMovement(movement);
        }
      } else {
        // If the product doesn't have a recipe, it might be a direct inventory item
        const inventoryItem = (state.inventoryItems || []).find(item => item.id === productId);
        
        if (inventoryItem) {
          // Create a stock movement for the direct inventory item
          const movement: Omit<StockMovement, 'id' | 'date'> = {
            inventoryItemId: productId,
            quantity: -1 * quantity, // Negative quantity for sale
            type: 'sale',
            notes: 'Direct product sale'
          };
          
          // Use the addStockMovement function from this actions object
          await actions.addStockMovement(movement);
        }
      }
    }
  };
  
  return actions;
};
